'use client';

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { TwitterProfileAnalysis } from '@/types/twitter-analysis';

interface UseTwitterProfileAnalysisReturn {
  analysis: TwitterProfileAnalysis | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch AI-generated profile analysis from Firestore
 * Listens for real-time updates (same pattern as useMarketAnalysis)
 * 
 * @param handleId - The handle ID (lowercase username)
 */
export function useTwitterProfileAnalysis(handleId: string | null): UseTwitterProfileAnalysisReturn {
  const [analysis, setAnalysis] = useState<TwitterProfileAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if no handleId or Firebase not available
    if (!handleId || !db || typeof window === 'undefined') {
      setLoading(false);
      setAnalysis(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Subscribe to profile analysis document
      const unsubscribe = onSnapshot(
        doc(db, 'twitterProfileAnalysis', handleId),
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data() as TwitterProfileAnalysis;
            setAnalysis(data);
            setError(null);
          } else {
            setAnalysis(null);
          }
          setLoading(false);
        },
        (err) => {
          console.warn(`Profile analysis listener error for ${handleId}:`, err);
          setError('Failed to fetch profile analysis');
          setLoading(false);
        }
      );

      return () => unsubscribe();
      
    } catch (err) {
      console.warn(`Failed to initialize profile analysis listener for ${handleId}:`, err);
      setError('Failed to initialize profile analysis');
      setLoading(false);
    }
  }, [handleId]);

  return { analysis, loading, error };
}

/**
 * Helper: Check if analysis is stale (older than N hours)
 */
export function isAnalysisStale(analysis: TwitterProfileAnalysis | null, maxAgeHours: number = 24): boolean {
  if (!analysis?.generatedAt) return true;
  
  const now = new Date();
  const then = new Date(analysis.generatedAt);
  const diffHours = (now.getTime() - then.getTime()) / (1000 * 60 * 60);
  
  return diffHours > maxAgeHours;
}

/**
 * Helper: Get time ago string from ISO timestamp
 */
export function getAnalysisTimeAgo(timestamp: string): string {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'yesterday';
  return `${diffDays}d ago`;
}

/**
 * Hook to trigger analysis for a handle
 * Returns a function to trigger the analysis
 */
export function useTriggerAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const triggerAnalysis = async (handleId: string, force: boolean = false): Promise<TwitterProfileAnalysis | null> => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/twitter/analyze-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Internal-Request': 'true'
        },
        body: JSON.stringify({ handleId, force })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      return data.analysis;
    } catch (err: any) {
      setError(err.message || 'Failed to trigger analysis');
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const triggerBatchAnalysis = async (): Promise<boolean> => {
    setIsAnalyzing(true);
    setError(null);

    try {
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_API_URL || process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/twitter/analyze-all-profiles`, {
        method: 'POST',
        headers: {
          'X-Internal-Request': 'true'
        }
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Batch analysis failed');
      }

      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to trigger batch analysis');
      return false;
    } finally {
      setIsAnalyzing(false);
    }
  };

  return { triggerAnalysis, triggerBatchAnalysis, isAnalyzing, error };
}


